import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, History, Printer, QrCode, Truck, User } from 'lucide-react'
import type { ReactNode } from 'react'

import { FormSection } from '@/components/forms/form-section'
import { Button } from '@/components/ui/button'
import {
    ContractDeliveryStatusBadge,
    cdoStatusLabel,
} from '@/components/contract-delivery/contract-delivery-status-badge'
import { ContractDeliveryActions } from '@/components/contract-delivery/contract-delivery-actions'
import { openContractDeliverySlipPrint } from '@/components/contract-delivery/print-contract-delivery-slip'
import {
    useContractDelivery,
    useContractDeliveryTimeline,
} from '@/hooks/use-contract-delivery-query'
import { qrcodeApi } from '@/lib/api/qrcode-api'
import { formatDateTime } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/contract-deliveries/$id',
)({
    component: ContractDeliveryDetailPage,
})

function Row({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="flex justify-between gap-4">
            <span className="text-muted-foreground shrink-0">{label}</span>
            <span className="text-right">{value}</span>
        </div>
    )
}

function ContractDeliveryDetailPage() {
    const { id } = Route.useParams()
    const { data: order, isLoading } = useContractDelivery(id)
    const { data: timeline = [], isLoading: timelineLoading } =
        useContractDeliveryTimeline(id)

    if (isLoading || !order) {
        return (
            <div className="p-4 md:p-6 text-sm text-muted-foreground">
                {isLoading ? m.cdo_loading() : m.cdo_not_found()}
            </div>
        )
    }

    return (
        <div className="space-y-4 p-4 md:space-y-6 md:p-6">
            <div className="flex flex-wrap items-start gap-3">
                <Button asChild variant="ghost" size="icon">
                    <Link to="/contract-deliveries">
                        <ArrowLeft className="size-4" />
                    </Link>
                </Button>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-xl font-semibold font-mono">
                            {order.order_code}
                        </h1>
                        <ContractDeliveryStatusBadge status={order.status} />
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        {formatDateTime(order.created_at)}
                    </p>
                </div>
            </div>

            <ContractDeliveryActions order={order} />

            <div className="grid gap-5 lg:grid-cols-[minmax(320px,380px)_minmax(0,1fr)] lg:items-start">
                <div className="space-y-5 lg:col-start-1 lg:row-start-1">
                    <FormSection
                        icon={<User />}
                        title={m.cdo_section_recipient()}
                    >
                        <div className="space-y-1.5 text-sm">
                            <Row
                                label={m.cdo_field_name()}
                                value={order.recipient_name}
                            />
                            <Row
                                label={m.cdo_col_phone()}
                                value={
                                    <a
                                        href={`tel:${order.recipient_phone}`}
                                        className="text-primary hover:underline"
                                    >
                                        {order.recipient_phone}
                                    </a>
                                }
                            />
                            <Row
                                label={m.cdo_field_address()}
                                value={order.shipping_address}
                            />
                            {order.shipping_ward && (
                                <Row
                                    label={m.customer_ward_label()}
                                    value={order.shipping_ward}
                                />
                            )}
                            {order.shipping_province && (
                                <Row
                                    label={m.customer_province_label()}
                                    value={order.shipping_province}
                                />
                            )}
                            {order.note && (
                                <Row
                                    label={m.cdo_field_note()}
                                    value={order.note}
                                />
                            )}
                            <Row
                                label={m.cdo_field_carrier()}
                                value={order.shipping_carrier_name || '—'}
                            />
                            <Row
                                label={m.cdo_field_created_by()}
                                value={order.created_by_name || '—'}
                            />
                            <Row
                                label={m.cdo_field_created_at()}
                                value={formatDateTime(order.created_at)}
                            />
                        </div>
                    </FormSection>

                    <FormSection
                        icon={<QrCode />}
                        title={m.cdo_qr_title()}
                        action={
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                    openContractDeliverySlipPrint(
                                        order,
                                        qrcodeApi.getContractDeliveryQRCodeUrl(
                                            order.id,
                                        ),
                                    )
                                }
                            >
                                <Printer className="size-4" />
                                {m.cdo_qr_print()}
                            </Button>
                        }
                    >
                        <div className="flex flex-col items-center gap-2">
                            <img
                                src={qrcodeApi.getContractDeliveryQRCodeUrl(
                                    order.id,
                                )}
                                alt={order.order_code}
                                className="size-40 rounded-md border p-1"
                            />
                            <p className="text-center text-xs text-muted-foreground">
                                {m.cdo_qr_hint()}
                            </p>
                        </div>
                    </FormSection>

                    <FormSection
                        icon={<Truck />}
                        title={m.cdo_section_delivery()}
                    >
                        <div className="space-y-1.5 text-sm">
                            <Row
                                label={m.cdo_field_driver()}
                                value={order.driver_name || '—'}
                            />
                            {order.delivered_at && (
                                <Row
                                    label={m.cdo_field_delivered_at()}
                                    value={formatDateTime(order.delivered_at)}
                                />
                            )}
                            {order.fail_reason && (
                                <Row
                                    label={m.cdo_field_fail_reason()}
                                    value={order.fail_reason}
                                />
                            )}
                        </div>
                    </FormSection>
                </div>

                <div className="space-y-5 lg:col-start-2 lg:row-start-1">
                    <FormSection
                        icon={<History />}
                        title={m.cdo_section_history()}
                    >
                        {timelineLoading ? (
                            <p className="text-sm text-muted-foreground">
                                {m.cdo_loading()}
                            </p>
                        ) : timeline.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                {m.cdo_history_empty()}
                            </p>
                        ) : (
                            <ol className="space-y-3">
                                {[...timeline].reverse().map((log) => (
                                    <li
                                        key={log.id}
                                        className="flex items-start justify-between gap-4 text-sm"
                                    >
                                        <div>
                                            <span className="font-medium">
                                                {log.from_status
                                                    ? `${cdoStatusLabel(log.from_status)} → ${cdoStatusLabel(log.to_status)}`
                                                    : cdoStatusLabel(
                                                          log.to_status,
                                                      )}
                                            </span>
                                            {log.note && (
                                                <p className="text-xs text-muted-foreground">
                                                    {log.note}
                                                </p>
                                            )}
                                        </div>
                                        <span className="shrink-0 text-xs text-muted-foreground">
                                            {formatDateTime(log.created_at)}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </FormSection>
                </div>
            </div>
        </div>
    )
}
